import publicNames from "../shader/cacheable-shaders.json";
import {loadingState} from "./loading-state";
export type AssetVersion={hash:string;bytes:number};
export type AssetManifest={schema:number;revision:string;resources:Record<string,AssetVersion>;
  shaders:Record<string,AssetVersion>;shaderBundle:AssetVersion};
type Kind="resource"|"shader"|"bundle";
export const ASSET_CACHE_NAME="echoplay-robots-assets-v1";
const MANIFEST_URL="/api/asset-manifest";
const safeName=(name:string)=>name.length>0&&!/[\\/\0]/.test(name)&&!name.startsWith(".");
export const isCacheableShader=(name:string)=>name.endsWith(".wgsl")&&publicNames.includes(name.slice(0,-5));
const validVersion=(v:AssetVersion|undefined):v is AssetVersion=>!!v&&/^[a-f0-9]{64}$/.test(v.hash)&&Number.isSafeInteger(v.bytes)&&v.bytes>=0;
export function validManifest(value:unknown):value is AssetManifest{
  const m=value as AssetManifest;
  return !!m&&m.schema===1&&typeof m.revision==="string"&&!!m.resources&&!!m.shaders&&validVersion(m.shaderBundle)&&
    Object.entries(m.resources).every(([name,v])=>safeName(name)&&/\.(glb|png|jpe?g|webp|wav|mix|mp3|m4a|aac)$/i.test(name)&&validVersion(v))&&
    Object.entries(m.shaders).every(([name,v])=>isCacheableShader(name)&&validVersion(v));
}
export function versionedAssetURL(kind:Kind,name:string,version:AssetVersion){
  const base=kind==="bundle"?"/api/shader-bundle":`/api/${kind}/${encodeURIComponent(name)}`;
  return `${base}?v=${version.hash}`;
}
async function digest(bytes:ArrayBuffer):Promise<string>{
  return Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256",bytes)),b=>b.toString(16).padStart(2,"0")).join("");
}
async function readResponse(response:Response,progress:boolean,resource:string):Promise<ArrayBuffer>{
  if(!response.body){const bytes=await response.arrayBuffer();loadingState.activity(resource);return bytes;}
  const reader=response.body.getReader(),parts:Uint8Array[]=[];let size=0;
  for(;;){const {done,value}=await reader.read();if(done)break;
    parts.push(value);size+=value.byteLength;if(progress)loadingState.received(value.byteLength);
    if(value.byteLength)loadingState.activity(resource);}
  const bytes=new Uint8Array(size);let offset=0;
  for(const part of parts){bytes.set(part,offset);offset+=part.byteLength;}
  return bytes.buffer;
}
/** The browser cache stores complete verified responses, never range fragments
 * or private WGSL. Cache/quota failures degrade to network, not a broken game. */
export class AssetCache{
  private manifestPromise:Promise<AssetManifest>|null=null;
  private cachePromise:Promise<Cache|null>|null=null;
  private inflight=new Map<string,Promise<Response>>();
  private running=0;
  private queue:Array<()=>void>=[];
  private canPrefetch=true;
  private cache():Promise<Cache|null>{
    if(!this.cachePromise)this.cachePromise=(async()=>{
      try{
        if(typeof caches==="undefined"){loadingState.cacheUnavailable();return null;}
        return await caches.open(ASSET_CACHE_NAME);
      }
      catch{loadingState.cacheUnavailable();return null;}
    })();
    return this.cachePromise;
  }
  manifest():Promise<AssetManifest>{
    if(!this.manifestPromise)this.manifestPromise=(async()=>{
      const cache=await this.cache();
      try{
        const response=await fetch(MANIFEST_URL,{cache:"no-cache"});
        if(!response.ok)throw new Error("Could not check asset versions.");
        const value:unknown=await response.json();
        if(!validManifest(value))throw new Error("Invalid asset manifest. Rebuild the application.");
        if(cache){
          try{await cache.put(MANIFEST_URL,Response.json(value));}catch{this.canPrefetch=false;loadingState.cacheUnavailable();}
          void this.prune(cache,value).catch(()=>{});
        }
        return value;
      }catch(error){
        const previous=await cache?.match(MANIFEST_URL).catch(()=>undefined);
        const value:unknown=previous?await previous.json().catch(()=>null):null;
        if(validManifest(value))return value;
        throw error;
      }
    })().catch(error=>{this.manifestPromise=null;throw error;});
    return this.manifestPromise;
  }
  private async prune(cache:Cache,manifest:AssetManifest){
    const keep=new Set([MANIFEST_URL,versionedAssetURL("bundle","",manifest.shaderBundle)]);
    for(const [name,v] of Object.entries(manifest.resources))keep.add(versionedAssetURL("resource",name,v));
    for(const [name,v] of Object.entries(manifest.shaders))keep.add(versionedAssetURL("shader",name,v));
    for(const request of await cache.keys()){
      const u=new URL(request.url);
      if(!keep.has(u.pathname+u.search))await cache.delete(request);
    }
  }
  private async slot<T>(job:()=>Promise<T>):Promise<T>{
    if(this.running>=4)await new Promise<void>(resolve=>this.queue.push(resolve));
    else this.running++;
    try{return await job();}finally{const next=this.queue.shift();if(next)next();else this.running--;}
  }
  async fetch(kind:Kind,name:string):Promise<Response>{
    // A manifest can never authorize caching a private/unknown shader.
    if(kind==="shader"&&!isCacheableShader(name))
      throw new Error("Private shaders must bypass the persistent asset cache.");
    if(kind!=="bundle"&&!safeName(name))throw new Error("Invalid asset name");
    const manifest=await this.manifest();
    const version=kind==="bundle"?manifest.shaderBundle:
      (kind==="resource"?manifest.resources:manifest.shaders)[name];
    if(!validVersion(version))throw new Error(`Asset absent from version manifest: ${name}`);
    const url=versionedAssetURL(kind,name,version);
    let job=this.inflight.get(url);
    if(!job){
      job=this.load(url,name,version,kind);
      this.inflight.set(url,job);
      void job.finally(()=>this.inflight.delete(url)).catch(()=>{});
    }
    return (await job).clone();
  }
  /** Warm verified bytes only, not decoded audio, GPU objects, or another scene.
   * Skip speculative traffic without persistent storage instead of fetching twice. */
  async prefetchResource(name:string):Promise<boolean>{
    if(!this.canPrefetch||!await this.cache())return false;
    const manifest=await this.manifest().catch(()=>null);
    if(!this.canPrefetch||!manifest||!validVersion(manifest.resources[name]))return false;
    const response=await this.fetch("resource",name);
    await response.arrayBuffer();
    return this.canPrefetch;
  }
  private async load(url:string,name:string,version:AssetVersion,kind:Kind):Promise<Response>{
    const cache=await this.cache();
    if(cache){
      try{
        const hit=await cache.match(url);
        if(hit?.status===200){
          const bytes=await hit.arrayBuffer();
          if(bytes.byteLength===version.bytes&&await digest(bytes)===version.hash){
            loadingState.file(bytes.byteLength,true);
            loadingState.activity(name);
            return new Response(bytes,{headers:hit.headers});
          }
          await cache.delete(url);
        }
      }catch{/* unreadable storage is not a boot failure */}
    }
    return this.slot(async()=>{
      const response=kind==="resource"&&name.endsWith(".glb")?
        await this.fetchModel(url,name):await fetch(url,{cache:"default"});
      if(!response.ok)throw new Error(response.status===409?
        "The application was updated during loading. Reload to get the new files.":
        `Could not load ${name||"graphics"} (${response.status}).`);
      const bytes=await readResponse(response,!(kind==="resource"&&name.endsWith(".glb")),name);
      if(bytes.byteLength!==version.bytes||await digest(bytes)!==version.hash)
        throw new Error(`Asset verification failed: ${name||"graphics"}. Reload to retry.`);
      const result=new Response(bytes,{status:200,headers:{
        "Content-Type":response.headers.get("content-type")??"application/octet-stream",
        "X-Asset-Hash":version.hash,"Content-Length":String(bytes.byteLength)}});
      if(cache)try{await cache.put(url,result.clone());}catch{this.canPrefetch=false;loadingState.cacheUnavailable();}
      loadingState.file(bytes.byteLength,false);loadingState.activity(name);return result;
    });
  }
  private async fetchModel(url:string,name:string):Promise<Response>{
    const size=8*1024*1024;
    const first=await fetch(url,{cache:"default",headers:{Range:`bytes=0-${size-1}`}});
    if(first.status===200){
      const bytes=await readResponse(first,true,name);
      return new Response(bytes,{headers:first.headers});
    }
    if(first.status!==206)return first;
    const match=/^bytes 0-(\d+)\/(\d+)$/.exec(first.headers.get("content-range")??"");
    if(!match)throw new Error(`Invalid model range: ${name}`);
    const end=Number(match[1]),total=Number(match[2]),parts=[await readResponse(first,true,name)];
    if(end>=total||parts[0].byteLength!==end+1)throw new Error(`Incomplete model range: ${name}`);
    for(let start=end+1;start<total;start+=size){
      const last=Math.min(start+size-1,total-1);
      const response=await fetch(url,{cache:"default",headers:{Range:`bytes=${start}-${last}`}});
      if(response.status!==206||response.headers.get("content-range")!==`bytes ${start}-${last}/${total}`)
        throw new Error(`Invalid model range: ${name}`);
      const bytes=await readResponse(response,true,name);
      if(bytes.byteLength!==last-start+1)throw new Error(`Incomplete model range: ${name}`);
      parts.push(bytes);
    }
    const body=new Blob(parts,{type:first.headers.get("content-type")??"model/gltf-binary"});
    if(body.size!==total)throw new Error(`Incomplete model: ${name}`);
    return new Response(body,{status:200,headers:{"Content-Type":body.type}});
  }
}
export const assetCache=new AssetCache();
